import { scannerMessages } from "constants/scanner.messages";

import { type DriverActionResult } from "./adapters/driverEffects";
import { Xrp2iDriverSession } from "./runtime/Xrp2iDriverSession";

export const TT_XRP2I_DRIVER_NAME = "tt-xrp2i";
export const TT_XRP2I_CAPABILITIES = [scannerMessages.DEVICE_TIME];

interface DeviceLike {
  deviceId?: string;
  id?: string;
  name?: string;
}

export interface DriverLifecycleHooks {
  onConnected?: (deviceId: string) => DriverActionResult;
  onDisconnected?: (deviceId: string) => void;
  onTick?: (deviceId: string) => DriverActionResult;
  tickIntervalMs?: number;
}

const sessionByDeviceId = new Map<string, Xrp2iDriverSession>();

/**
 * Extracts a device identifier from a device object, preferring `deviceId` over the fallback `id` property. The two-field check exists because the same physical scanner can arrive under either name depending on which call site sends the message.
 */
function getDeviceId(device: DeviceLike): string | undefined {
  return device.deviceId ?? device.id;
}

/**
 * Returns the active session for a given device, creating one if none exists yet. Sessions are created on demand so the driver does not hold state for devices it has seen in a message but not yet connected to.
 */
function getOrCreateSession(deviceId: string): Xrp2iDriverSession {
  const existing = sessionByDeviceId.get(deviceId);
  if (existing) {
    return existing;
  }
  const session = new Xrp2iDriverSession(deviceId);
  sessionByDeviceId.set(deviceId, session);
  return session;
}

/**
 * Synchronises the scanner hardware state before normal interaction resumes. The sync reads the device's current line-ending and EID output-format settings so the protocol parser can interpret live scan frames correctly.
 */
export function truTestXRP2ISyncState(deviceId: string): DriverActionResult {
  return getOrCreateSession(deviceId).syncState();
}

/**
 * Destroys the driver session for a device and removes it from the session cache. Each session holds a transport subscription, so it must be explicitly cancelled when the device disconnects.
 */
export function truTestXRP2IDestroySession(deviceId: string): void {
  const session = sessionByDeviceId.get(deviceId);
  if (!session) {
    return;
  }
  session.destroy();
  sessionByDeviceId.delete(deviceId);
}

/**
 * Drives the periodic heartbeat for a connected device. The tick does nothing when no session exists, which covers the gap between a disconnect and the next time the scheduler fires.
 */
function truTestXRP2ITick(deviceId: string): DriverActionResult {
  const session = sessionByDeviceId.get(deviceId);
  if (!session) {
    return;
  }
  return session.tick();
}

/**
 * Routes a raw scanner payload into the active session for the matching device. The loose device type exists because the same physical scanner can arrive with either `deviceId` or `id` set, depending on the caller.
 */
export const truTestXRP2IProcessMessage = (
  device: DeviceLike,
  _type: string,
  _status: unknown,
  serializedPayload: string,
): DriverActionResult => {
  const deviceId = getDeviceId(device);
  if (!deviceId) {
    return;
  }
  return getOrCreateSession(deviceId).receive(serializedPayload);
};

/**
 * Queries the device time, then writes the corrected time. Warns the operator if the clock was significantly out before correction. The call is silently skipped when no session exists.
 */
function truTestXRP2ISetDeviceTime(deviceId: string): DriverActionResult {
  const session = sessionByDeviceId.get(deviceId);
  if (!session) {
    return;
  }
  return session.setDeviceTime();
}

export const TT_XRP2I_HANDLER = {
  [scannerMessages.DEVICE_TIME]: ({ deviceId }: { deviceId: string }) =>
    truTestXRP2ISetDeviceTime(deviceId),
};

function truTestXRP2IOnConnect(deviceId: string): DriverActionResult {
  const syncResult = truTestXRP2ISyncState(deviceId);
  const timeResult = truTestXRP2ISetDeviceTime(deviceId);
  if (!syncResult && !timeResult) {
    return;
  }
  if (!syncResult) {
    return timeResult;
  }
  if (!timeResult) {
    return syncResult;
  }
  return [...syncResult, ...timeResult];
}

export const TT_XRP2I_DRIVER_LIFECYCLE: DriverLifecycleHooks = {
  onConnected: truTestXRP2IOnConnect,
  onDisconnected: truTestXRP2IDestroySession,
  onTick: truTestXRP2ITick,
  tickIntervalMs: 1000,
};
